/**
 * Iris scorecards — weighted review rubrics used to grade the AI service
 * agent's conversations. A scorecard is a set of weighted criteria (name +
 * weight + a "critical" flag where a single failure fails the whole review)
 * plus a pass threshold (0-100). Reviewers score a conversation against a
 * scorecard to get a single weighted result.
 *
 * Proxied through operations at `/api/messaging/iris/scorecards` (operations is
 * the FE's auth boundary + RBAC gate; it forwards to ai-operations with the
 * shared service key), mirroring the sibling guidance/review hooks.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPost, apiPut, operationsClient } from '@/lib/api';

const BASE = '/api/messaging/iris/scorecards';

/** One weighted line item on a scorecard. */
export interface ScorecardCriterion {
  /**
   * What the reviewer is grading (e.g. "Confirmed the address"). This is the
   * criterion's identity, not just its label — reviews record grades in a map
   * keyed by it — so it must be unique within a scorecard.
   */
  name: string;
  /** Relative weight in the overall score. */
  weight: number;
  /** When true, a single fail on this criterion fails the whole review. */
  critical: boolean;
}

/**
 * The first duplicated criterion name in a rubric, or null when all are
 * distinct. Two criteria sharing a name share a single grade, which the backend
 * then counts twice under both weights — so such a rubric can't be graded
 * reliably. The API rejects duplicates on create/update; this also catches the
 * scorecards saved before that rule existed, which are still in the database.
 * Compared case- and whitespace-insensitively, matching the API.
 */
export function duplicateCriterionName(criteria: ScorecardCriterion[]): string | null {
  const seen = new Set<string>();
  for (const c of criteria) {
    const key = c.name.trim().toLowerCase();
    if (!key) continue;
    if (seen.has(key)) return c.name.trim();
    seen.add(key);
  }
  return null;
}

export interface Scorecard {
  id: string;
  name: string;
  /** Minimum weighted score (0-100) needed to pass. */
  passThreshold: number;
  criteria: ScorecardCriterion[];
  enabled: boolean;
}

export interface CreateScorecardPayload {
  name: string;
  passThreshold: number;
  criteria: ScorecardCriterion[];
  enabled?: boolean;
}

export interface UpdateScorecardPayload {
  name?: string;
  passThreshold?: number;
  criteria?: ScorecardCriterion[];
  enabled?: boolean;
}

export const irisScorecardsKey = ['messaging', 'iris-scorecards'] as const;

export function useScorecards() {
  return useQuery({
    queryKey: irisScorecardsKey,
    // The backend wraps the list in { items } (shared Iris list contract).
    queryFn: () => apiGet<{ items: Scorecard[] }>(operationsClient, BASE).then((r) => r.items),
    staleTime: 30_000,
  });
}

export function useCreateScorecard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateScorecardPayload) =>
      apiPost<Scorecard, CreateScorecardPayload>(operationsClient, BASE, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: irisScorecardsKey });
    },
  });
}

export function useUpdateScorecard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: string } & UpdateScorecardPayload) =>
      apiPut<Scorecard, UpdateScorecardPayload>(
        operationsClient,
        `${BASE}/${encodeURIComponent(id)}`,
        payload,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: irisScorecardsKey });
    },
  });
}

export function useDeleteScorecard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiDelete<void>(operationsClient, `${BASE}/${encodeURIComponent(id)}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: irisScorecardsKey });
    },
  });
}
